import { makeAutoObservable, runInAction } from 'mobx';
import {
  IAuthDataSource,
  RestoreStep,
  UNEXPECTED_ERROR_KEY,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import { SubmittingStatus } from '../../../../../../shared/infrastructure/types/common/common-types';
import { ResponseStatus } from '../../../../../../shared/infrastructure/types/api/api-types';
import {
  RecoveryErrorData,
  RecoveryResetErrorData,
} from '../../../../../infrastructure/types/features/auth/auth-contracts';
import { RestorePasswordValidator } from '../validators/RestorePasswordValidator';

class RestorePasswordManager {
  email?: LoginEmail;
  newPassword?: LoginPassword;
  passwordConfirmation?: LoginPassword;
  resetPasswordToken?: string;
  recoveryEmailError?: string;
  globalError?: string;
  passwordServerError?: string;
  passwordConfirmationServerError?: string;
  step: RestoreStep = RestoreStep.ENTER_EMAIL;

  validator: RestorePasswordValidator;

  constructor(
    private datasource: IAuthDataSource,
    private getFormStatus: () => SubmittingStatus,
    private setFormStatus: (status: SubmittingStatus) => void,
    private navigateToLogin: () => void,
  ) {
    makeAutoObservable(this, {});
    this.validator = new RestorePasswordValidator(this);
  }

  get formStatus() {
    return this.getFormStatus();
  }

  setEmail(email: LoginEmail) {
    this.email = email;
    this.recoveryEmailError = undefined;
  }

  setNewPassword(password: LoginPassword) {
    this.newPassword = password;
    this.passwordServerError = undefined;
  }

  setPasswordConfirmation(password: LoginPassword) {
    this.passwordConfirmation = password;
    this.passwordConfirmationServerError = undefined;
  }

  setResetParams(resetPasswordToken: string, email: LoginEmail) {
    this.resetPasswordToken = resetPasswordToken;
    this.email = email as LoginEmail;
    this.step = RestoreStep.NEW_PASSWORD;
    this.clearResetServerErrors();

    this.datasource.validateResetPasswordToken(resetPasswordToken).then(response => {
      runInAction(() => {
        if (response.status === ResponseStatus.SUCCESS) {
          this.email = response.result?.email ?? this.email;
          return;
        }
        this.applyResetError(response.status, response);
      });
    });
  }

  reset() {
    this.email = undefined;
    this.newPassword = undefined;
    this.passwordConfirmation = undefined;
    this.resetPasswordToken = undefined;
    this.recoveryEmailError = undefined;
    this.clearResetServerErrors();
    this.step = RestoreStep.ENTER_EMAIL;
    this.setFormStatus(SubmittingStatus.UNTOUCHED);
  }

  private clearResetServerErrors() {
    this.globalError = undefined;
    this.passwordServerError = undefined;
    this.passwordConfirmationServerError = undefined;
  }

  private applyResetError(status: ResponseStatus, response) {
    if (status === ResponseStatus.SERVICE_ERROR) {
      const data = response.error.errorData as RecoveryResetErrorData;
      if (data?.error) {
        this.globalError = data.error;
        return;
      }
      if (data?.errors) {
        this.globalError = data.errors.email;
        this.passwordServerError = data.errors.password;
        this.passwordConfirmationServerError = data.errors.password_confirmation;
        return;
      }
    }
    this.globalError = UNEXPECTED_ERROR_KEY;
  }

  submitResetPassword() {
    if (this.validator.hasResetErrors) {
      return this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
    }

    this.setFormStatus(SubmittingStatus.SUBMITTING);
    this.clearResetServerErrors();

    this.datasource
      .resetPassword({
        email: this.email as LoginEmail,
        reset_password_token: this.resetPasswordToken as string,
        password: this.newPassword as LoginPassword,
        password_confirmation: this.passwordConfirmation as LoginPassword,
      })
      .then(response => {
        runInAction(() => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.setFormStatus(SubmittingStatus.UNTOUCHED);
            this.navigateToLogin();
            return;
          }
          this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
          this.applyResetError(response.status, response);
        });
      });
  }

  submitRecoveryRequest() {
    if (this.validator.hasErrors) {
      return this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
    }

    this.setFormStatus(SubmittingStatus.SUBMITTING);
    this.recoveryEmailError = undefined;

    this.datasource.requestRecoveryEmail({ email: this.email as LoginEmail }).then(response => {
      runInAction(() => {
        if (response.status === ResponseStatus.SUCCESS) {
          const result = response.result as unknown as RecoveryErrorData;
          if (result?.errors?.email || result?.error) {
            this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
            if (result.errors?.email) {
              this.recoveryEmailError = result.errors?.email;
            }
            return;
          }
          this.step = RestoreStep.WAIT_EMAIL;
          this.setFormStatus(SubmittingStatus.UNTOUCHED);
          return;
        }

        this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);

        if (response.status === ResponseStatus.SERVICE_ERROR) {
          const data = response.error.errorData as RecoveryErrorData;
          this.recoveryEmailError = data?.errors?.email ?? data?.error ?? UNEXPECTED_ERROR_KEY;
          return;
        }

        this.recoveryEmailError = UNEXPECTED_ERROR_KEY;
      });
    });
  }
}

export { RestorePasswordManager };
